import { useAppleSignIn, type UseAppleSignInOptions } from '../login/useAppleSignIn';
import type { LoginOption } from '../login/loginConstants';
import { useAuth } from '../auth/AuthContext';
import { translateLoginOption, useI18n, type MessageKey } from '../i18n';
import styles from '../pages/AuthLoginPortal.module.css';

function AppleIcon() {
  return (
    <svg className={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 12.67c-.02-2.17 1.77-3.21 1.85-3.27-1.01-1.47-2.58-1.67-3.14-1.7-1.34-.14-2.62.79-3.3.79-.68 0-1.73-.77-2.85-.75-1.47.02-2.82.85-3.57 2.16-1.52 2.64-.39 6.55 1.09 8.7.72 1.04 1.58 2.2 2.71 2.16 1.09-.04 1.5-.7 2.82-.7 1.32 0 1.68.7 2.83.68 1.17-.02 1.91-1.05 2.62-2.09.83-1.2 1.17-2.37 1.19-2.43-.03-.01-2.28-.87-2.3-3.45zm-2.14-6.5c.59-.71.99-1.7.88-2.69-.85.03-1.88.57-2.49 1.28-.55.64-1.03 1.67-.9 2.65.95.07 1.92-.48 2.51-1.24z" />
    </svg>
  );
}

export type AuthAppleSignInBlockProps = {
  loginType: LoginOption;
  introKey?: MessageKey;
  disabled?: boolean;
} & Pick<UseAppleSignInOptions, 'registerPrefillOnMiss' | 'onRegisterPrefill'>;

export function AuthAppleSignInBlock({
  loginType,
  introKey,
  disabled = false,
  registerPrefillOnMiss,
  onRegisterPrefill,
}: AuthAppleSignInBlockProps) {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const {
    isConfigured: appleConfigured,
    submitting: appleSubmitting,
    errorFriendly: appleErrorFriendly,
    errorDetails: appleErrorDetails,
    requestAppleSignIn,
  } = useAppleSignIn(loginType, { registerPrefillOnMiss, onRegisterPrefill });

  if (!appleConfigured) {
    return (
      <div className={styles.appleSignInBlock}>
        <button
          type="button"
          className={styles.socialBtnApple}
          disabled
          title={t('auth.portal.appleNotConfigured')}
        >
          <AppleIcon />
          <span>{t('auth.portal.apple')}</span>
        </button>
        <p className={styles.appleSetupHint}>{t('auth.portal.appleNotConfigured')}</p>
      </div>
    );
  }

  const introText = introKey
    ? t(introKey, { type: translateLoginOption(loginType, t) })
    : null;

  return (
    <div className={styles.appleSignInBlock}>
      {introText ? <p className={styles.appleRoleHint}>{introText}</p> : null}
      <button
        type="button"
        className={styles.socialBtnAppleActive}
        onClick={() => void requestAppleSignIn()}
        disabled={disabled || appleSubmitting || isLoggedIn}
        aria-busy={appleSubmitting}
      >
        <AppleIcon />
        <span>{appleSubmitting ? t('auth.portal.appleSigningIn') : t('auth.portal.apple')}</span>
      </button>
      {appleErrorFriendly ? (
        <div className={styles.failureBlock} role="alert">
          <p className={styles.friendlyError}>{appleErrorFriendly}</p>
          {appleErrorDetails ? (
            <details className={styles.errorDetails}>
              <summary className={styles.errorDetailsSummary}>{t('common.technicalDetails')}</summary>
              <pre className={styles.technicalPre}>{appleErrorDetails}</pre>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
